// src/data/glossary.ts
// PageOne Lawyers — Law Firm SEO Glossary content
// Author: Aditya Chauhan, Head of SEO, PageOne Lawyers

export type GlossaryCategory =
  | "Local SEO"
  | "Technical SEO"
  | "Content & On-Page"
  | "Link Building"
  | "AI Search & GEO"
  | "PPC & Paid Search"
  | "Leads & Analytics"
  | "Bar Compliance";

export interface GlossaryLink {
  label: string;
  href: string;
}

export interface GlossaryTerm {
  slug: string;
  term: string;
  category: GlossaryCategory;
  metaDescription: string;
  shortDefinition: string; // 40–60 words, shown first; snippet / AI-citation target
  whyItMatters: string;
  lawFirmExample: string;
  howToImprove: string[];
  relatedTerms: string[]; // slugs
  service: GlossaryLink;
  blog?: GlossaryLink;
}

export const glossaryHub = {
  path: "/glossary",
  metaTitle: "Law Firm SEO Glossary: 37 Terms Explained | PageOne Lawyers",
  metaDescription:
    "Plain-English definitions of the SEO, local search, AI search, PPC and bar-compliance terms every law firm partner should know. Written for attorneys.",
  h1: "Law Firm SEO Glossary",
  intro:
    "Your SEO agency should never hide behind jargon. This glossary explains the terms you will see in audits, reports and strategy calls, in plain English and with examples from real practice areas. Each entry covers what the term means, why it matters for a law firm, and what you can do about it.",
  author: "Aditya Chauhan",
  authorTitle: "Head of SEO, PageOne Lawyers",
  lastUpdated: "2026-09-26",
};

export const glossaryCategories: GlossaryCategory[] = [
  "Local SEO",
  "Technical SEO",
  "Content & On-Page",
  "Link Building",
  "AI Search & GEO",
  "PPC & Paid Search",
  "Leads & Analytics",
  "Bar Compliance",
];

export const glossaryTerms: GlossaryTerm[] = [
  // ───────────────────────── LOCAL SEO ─────────────────────────
  {
    slug: "local-pack",
    term: "Local Pack",
    category: "Local SEO",
    metaDescription:
      "What is the Local Pack? The map results Google shows for local searches, and why ranking in them drives calls for law firms.",
    shortDefinition:
      "The Local Pack is the block of three business listings with a map that Google shows for searches with local intent, such as 'DUI lawyer near me'. It appears above most organic results and pulls its data from Google Business Profiles rather than from law firm websites.",
    whyItMatters:
      "For most practice areas, the Local Pack captures a large share of clicks and calls on mobile. Searchers can call a firm directly from the listing without ever visiting the website. A firm that ranks well organically but is missing from the Local Pack is invisible to many of the highest-intent searchers in its market.",
    lawFirmExample:
      "A criminal defense firm in Phoenix ranks #2 organically for 'DUI lawyer Phoenix' but is not in the Local Pack. Most after-hours mobile calls go to the three firms shown on the map, because the caller taps 'Call' straight from the results page.",
    howToImprove: [
      "Fully complete and verify your Google Business Profile with the correct primary category.",
      "Keep your firm name, address and phone number identical everywhere online.",
      "Earn a steady flow of genuine client reviews and reply to them.",
      "Build a strong location page on your website that matches the profile's address.",
    ],
    relatedTerms: ["google-business-profile", "nap-consistency", "review-velocity", "local-citations"],
    service: { label: "Local SEO for Law Firms", href: "/services/local-seo-for-law-firms" },
    blog: { label: "DUI/DWI Lawyer SEO Guide", href: "/blog/dui-lawyer-seo-guide" },
  },
  {
    slug: "google-business-profile",
    term: "Google Business Profile (GBP)",
    category: "Local SEO",
    metaDescription:
      "What is a Google Business Profile? The free listing that powers your firm's map and Local Pack visibility on Google.",
    shortDefinition:
      "A Google Business Profile is the free listing that controls how a law firm appears on Google Maps and in the Local Pack. It holds the firm's name, address, phone number, hours, categories, reviews, photos and services, and is one of the strongest factors in local rankings.",
    whyItMatters:
      "Google uses the profile to decide which firms to show for local searches and how prominently. The primary category matters most: a firm listed as 'Law firm' competes differently from one listed as 'Personal injury attorney'. Incomplete, unverified or suspended profiles lose local visibility almost entirely.",
    lawFirmExample:
      "An estate planning firm in Boston changes its primary category from 'Lawyer' to 'Estate planning attorney' and adds its probate and trust services. Within weeks it begins appearing for 'probate lawyer' searches it never showed for before.",
    howToImprove: [
      "Choose the most specific primary category for your main practice area.",
      "Add secondary categories only for areas you actually practise.",
      "List each practice area as a service with a short description.",
      "Post updates and upload real photos of your office and attorneys.",
    ],
    relatedTerms: ["local-pack", "nap-consistency", "review-velocity"],
    service: { label: "Local SEO for Law Firms", href: "/services/local-seo-for-law-firms" },
  },
  {
    slug: "nap-consistency",
    term: "NAP Consistency",
    category: "Local SEO",
    metaDescription:
      "What is NAP consistency? Why your firm's name, address and phone number must match everywhere online for local SEO.",
    shortDefinition:
      "NAP consistency means a law firm's Name, Address and Phone number appear exactly the same across its website, Google Business Profile, legal directories and other listings. Consistent NAP data helps Google trust that the business information is accurate.",
    whyItMatters:
      "Conflicting details, such as an old suite number or a tracking number on some listings and the main line on others, create uncertainty about where and who the firm is. This can weaken local rankings and send potential clients to a wrong number or old office.",
    lawFirmExample:
      "A family law firm moved offices two years ago, but Avvo, Justia and several directories still show the old address. After cleaning up those listings to match the new address, its map rankings in the new neighbourhood stabilise.",
    howToImprove: [
      "Pick one official format for your firm name, address and phone number.",
      "Use that exact format in your website footer and contact page.",
      "Audit and correct legal directories such as Avvo, Justia, FindLaw and Martindale.",
      "Use call tracking numbers carefully so the main number stays consistent on core listings.",
    ],
    relatedTerms: ["local-citations", "google-business-profile", "call-tracking"],
    service: { label: "Local SEO for Law Firms", href: "/services/local-seo-for-law-firms" },
  },
  {
    slug: "local-citations",
    term: "Local Citations",
    category: "Local SEO",
    metaDescription:
      "What are local citations? Online mentions of your law firm's name, address and phone number, and how they support local rankings.",
    shortDefinition:
      "A local citation is any online mention of a law firm's name, address and phone number, usually on directories such as Avvo, Justia, FindLaw, Yelp or a local bar association site. Citations help confirm a firm's location and legitimacy to search engines.",
    whyItMatters:
      "Legal directories are some of the most trusted citation sources and often rank on page one themselves. Being listed accurately on the right ones supports Local Pack rankings and gives potential clients another route to find you.",
    lawFirmExample:
      "A new immigration firm has a Google Business Profile but no directory listings. Adding complete profiles on major legal directories and its state bar's lawyer referral listing gives Google consistent signals about the firm's location.",
    howToImprove: [
      "Prioritise quality legal directories over hundreds of low-value listings.",
      "Complete every field, including practice areas and attorney bios.",
      "Keep NAP details identical to your Google Business Profile.",
      "Remove or merge duplicate listings.",
    ],
    relatedTerms: ["nap-consistency", "local-pack", "backlink"],
    service: { label: "Local SEO for Law Firms", href: "/services/local-seo-for-law-firms" },
  },
  {
    slug: "review-velocity",
    term: "Review Velocity",
    category: "Local SEO",
    metaDescription:
      "What is review velocity? The pace at which your firm earns new Google reviews, and why a steady flow beats a one-time burst.",
    shortDefinition:
      "Review velocity is the rate at which a law firm earns new online reviews, most importantly on its Google Business Profile. A steady, ongoing flow of genuine reviews signals an active, trusted practice to both searchers and search engines.",
    whyItMatters:
      "A firm with 200 reviews but none in the last year can look less active than a competitor earning a few every month. Recent reviews also affect whether people click and call. Sudden spikes of reviews can look unnatural and may trigger review filtering.",
    lawFirmExample:
      "A personal injury firm builds a simple follow-up step into case closing: the paralegal sends a review link after a settlement is paid. The firm moves from a handful of reviews a year to several a month.",
    howToImprove: [
      "Ask every satisfied client for a review at a consistent point in the case lifecycle.",
      "Send a direct review link by email or text.",
      "Reply to every review professionally without disclosing confidential case details.",
      "Never buy reviews or offer incentives, which breaks Google policy and may breach bar rules.",
    ],
    relatedTerms: ["google-business-profile", "local-pack", "client-testimonial-rules"],
    service: { label: "Local SEO for Law Firms", href: "/services/local-seo-for-law-firms" },
  },

  // ───────────────────────── TECHNICAL SEO ─────────────────────────
  {
    slug: "crawl-budget",
    term: "Crawl Budget",
    category: "Technical SEO",
    metaDescription:
      "What is crawl budget? How many pages Googlebot will crawl on your law firm site, and when it actually matters.",
    shortDefinition:
      "Crawl budget is the number of pages Googlebot is willing and able to crawl on a website in a given period. It depends on how fast the server responds and how much Google wants to crawl the site. It mainly becomes a concern for large sites with thousands of URLs.",
    whyItMatters:
      "Most small and mid-sized law firm websites do not have a crawl budget problem. It becomes relevant when a site has large numbers of location pages, filtered or duplicate URLs, or old blog archives, so that important practice area pages are crawled less often.",
    lawFirmExample:
      "A multi-state firm generates a separate page for every city and practice area combination, creating thousands of thin URLs. Googlebot spends time on those pages while updated core practice pages take weeks to be re-crawled.",
    howToImprove: [
      "Remove or noindex thin, duplicate and auto-generated pages.",
      "Keep your XML sitemap limited to pages you want indexed.",
      "Fix redirect chains and broken internal links.",
      "Improve server response time.",
    ],
    relatedTerms: ["indexing", "canonical-tag", "content-pruning"],
    service: { label: "Technical SEO for Law Firms", href: "/services/technical-seo-for-law-firms" },
  },
  {
    slug: "indexing",
    term: "Indexing",
    category: "Technical SEO",
    metaDescription:
      "What is indexing in SEO? How Google stores your law firm's pages, and why unindexed pages can never rank.",
    shortDefinition:
      "Indexing is the process by which Google stores a page in its search index after crawling it. Only indexed pages can appear in search results. A page can be crawled but not indexed if Google judges it low quality, duplicate, or blocked by a noindex tag.",
    whyItMatters:
      "If a practice area page is not indexed, it cannot rank for anything, no matter how good the content is. Indexing problems are common after site redesigns, platform migrations and on JavaScript-heavy sites where content does not load for Google.",
    lawFirmExample:
      "After a website rebuild, a bankruptcy firm's Chapter 13 page shows 'Crawled, currently not indexed' in Search Console. The page turns out to be a near-duplicate of the Chapter 7 page and needs unique content to be indexed.",
    howToImprove: [
      "Check the Pages report in Google Search Console regularly.",
      "Use URL Inspection to confirm Google sees your full content.",
      "Make sure key pages are not blocked by robots.txt or a noindex tag.",
      "Give each page a distinct purpose and unique content.",
    ],
    relatedTerms: ["crawl-budget", "canonical-tag", "practice-area-page"],
    service: { label: "Technical SEO for Law Firms", href: "/services/technical-seo-for-law-firms" },
  },
  {
    slug: "core-web-vitals",
    term: "Core Web Vitals",
    category: "Technical SEO",
    metaDescription:
      "What are Core Web Vitals? Google's page experience metrics (LCP, INP, CLS) and what they mean for law firm websites.",
    shortDefinition:
      "Core Web Vitals are three Google metrics that measure real-user page experience: Largest Contentful Paint (loading, good at 2.5 seconds or less), Interaction to Next Paint (responsiveness, good at 200 ms or less) and Cumulative Layout Shift (visual stability, good at 0.1 or less).",
    whyItMatters:
      "Core Web Vitals are a minor ranking factor on their own, but slow and jumpy pages lose potential clients. Someone searching after an accident or arrest is usually on a phone and will leave a slow page and call the next firm.",
    lawFirmExample:
      "A workers' compensation firm's homepage loads a large autoplay video hero. Its mobile LCP is over 5 seconds. Replacing the video with a compressed image brings LCP under 2.5 seconds and reduces bounce on mobile.",
    howToImprove: [
      "Compress and correctly size images; use modern formats such as WebP.",
      "Limit heavy scripts, chat widgets and third-party tags.",
      "Reserve space for images and embeds to prevent layout shift.",
      "Track field data in the Search Console Core Web Vitals report.",
    ],
    relatedTerms: ["indexing", "intake-conversion-rate"],
    service: { label: "Technical SEO for Law Firms", href: "/services/technical-seo-for-law-firms" },
  },
  {
    slug: "canonical-tag",
    term: "Canonical Tag",
    category: "Technical SEO",
    metaDescription:
      "What is a canonical tag? How rel=canonical tells Google which version of a law firm page to index and rank.",
    shortDefinition:
      "A canonical tag (rel='canonical') is a line of HTML that tells search engines which URL is the preferred version of a page when similar or duplicate versions exist. Google treats it as a strong hint when choosing which URL to index and show in results.",
    whyItMatters:
      "Law firm sites often create duplicates without realising it, through tracking parameters, www and non-www versions, trailing slashes or printer-friendly pages. Without correct canonicals, ranking signals can be split across versions.",
    lawFirmExample:
      "An employment law firm runs Google Ads to its wrongful termination page with tracking parameters. Every page self-references its clean URL as canonical, so only the clean version is indexed.",
    howToImprove: [
      "Add a self-referencing canonical to every indexable page.",
      "Point duplicates and parameter URLs to the clean version.",
      "Make sure canonical URLs return a 200 status and are not redirected.",
      "Keep canonicals consistent with your sitemap and internal links.",
    ],
    relatedTerms: ["indexing", "crawl-budget"],
    service: { label: "Technical SEO for Law Firms", href: "/services/technical-seo-for-law-firms" },
  },
  {
    slug: "schema-markup",
    term: "Schema Markup",
    category: "Technical SEO",
    metaDescription:
      "What is schema markup? Structured data that helps Google and AI search understand your law firm, attorneys and services.",
    shortDefinition:
      "Schema markup is structured data, usually written in JSON-LD, that describes a page's content in a standard vocabulary from Schema.org. It helps search engines and AI systems understand who a law firm is, where it is, which attorneys work there and what services it offers.",
    whyItMatters:
      "Schema does not directly boost rankings, but it helps Google connect your firm, attorneys and locations as clear entities. It can also make pages eligible for rich results such as breadcrumbs and review snippets, and gives AI search tools cleaner facts to cite.",
    lawFirmExample:
      "A tax law firm adds LegalService schema to its homepage, Person schema to each attorney bio, and BreadcrumbList schema sitewide. Its attorney names begin appearing correctly in knowledge-style results.",
    howToImprove: [
      "Add LegalService or Attorney schema to your homepage and location pages.",
      "Add Person schema to attorney bio pages.",
      "Validate markup with Google's Rich Results Test.",
      "Only mark up information that is visible on the page.",
    ],
    relatedTerms: ["legalservice-schema", "entity-seo", "e-e-a-t"],
    service: { label: "Technical SEO for Law Firms", href: "/services/technical-seo-for-law-firms" },
  },
  {
    slug: "legalservice-schema",
    term: "LegalService Schema",
    category: "Technical SEO",
    metaDescription:
      "What is LegalService schema? The Schema.org type that describes a law firm's name, location, practice areas and contact details.",
    shortDefinition:
      "LegalService is a Schema.org type for businesses that provide legal services. Law firms use it in JSON-LD to describe their name, address, phone, opening hours, service area and practice areas in a format search engines can read reliably.",
    whyItMatters:
      "It reinforces the same facts shown in your Google Business Profile and on your website, which strengthens local and entity signals. For multi-office firms, separate LegalService markup on each location page helps Google tie each office to the right city.",
    lawFirmExample:
      "A real estate law firm with offices in Dallas and Austin adds a LegalService block to each location page, with that office's address, phone and areaServed. Each office's page aligns with its own Google Business Profile.",
    howToImprove: [
      "Include name, url, telephone, address, geo, openingHours and areaServed.",
      "Use sameAs to link your GBP and major legal directory profiles.",
      "Use one block per physical office on its own location page.",
      "Match every value exactly to your NAP details.",
    ],
    relatedTerms: ["schema-markup", "nap-consistency", "google-business-profile"],
    service: { label: "Technical SEO for Law Firms", href: "/services/technical-seo-for-law-firms" },
  },

  // ───────────────────────── CONTENT & ON-PAGE ─────────────────────────
  {
    slug: "practice-area-page",
    term: "Practice Area Page",
    category: "Content & On-Page",
    metaDescription:
      "What is a practice area page? The core service page that ranks for your most valuable legal searches and converts visitors into consultations.",
    shortDefinition:
      "A practice area page is a dedicated page on a law firm website describing one area of law the firm handles, such as car accident claims or DUI defense. It is usually the page that ranks for high-value commercial searches and converts visitors into consultations.",
    whyItMatters:
      "Google ranks pages, not firms. A single 'Practice Areas' page listing ten services in a few lines each rarely ranks for any of them. Separate, detailed pages for each practice area, and often for important sub-types, give each one a real chance to rank.",
    lawFirmExample:
      "A personal injury firm replaces one generic 'Personal Injury' page with separate pages for car accidents, truck accidents, slip and fall, and wrongful death. Each page answers the specific questions clients ask about that case type.",
    howToImprove: [
      "Create one page per practice area and major case type.",
      "Answer real client questions: process, timelines, costs, what to do next.",
      "Show attorney credentials and relevant results, following bar rules.",
      "Include a clear call to action and internal links to related guides.",
    ],
    relatedTerms: ["search-intent", "topic-cluster", "e-e-a-t"],
    service: { label: "Legal Content Writing", href: "/services/legal-content-writing" },
    blog: { label: "Law Firm SEO Strategy", href: "/blog/law-firm-seo-strategy" },
  },
  {
    slug: "e-e-a-t",
    term: "E-E-A-T",
    category: "Content & On-Page",
    metaDescription:
      "What is E-E-A-T? Experience, Expertise, Authoritativeness and Trust, and why Google holds legal content to a high standard.",
    shortDefinition:
      "E-E-A-T stands for Experience, Expertise, Authoritativeness and Trustworthiness. It comes from Google's Search Quality Rater Guidelines and describes the qualities Google looks for in helpful content, with trust being the most important. It is a framework, not a single ranking score.",
    whyItMatters:
      "Legal topics affect people's rights, money and freedom, so Google applies higher standards to them. Content with no identifiable author, no credentials and no evidence of real practice experience struggles to compete with firms that show who wrote and reviewed it.",
    lawFirmExample:
      "A family law firm adds attorney bylines, 'reviewed by' notes, bar admission details and years of experience to its divorce guides. It also links each guide to the reviewing attorney's full bio page.",
    howToImprove: [
      "Add author or reviewer bylines linked to detailed attorney bios.",
      "Show bar admissions, education, memberships and relevant experience.",
      "Share first-hand insight from real cases, without confidential details.",
      "Keep content accurate and show a last-updated date.",
    ],
    relatedTerms: ["ymyl", "schema-markup", "entity-seo"],
    service: { label: "Legal Content Writing", href: "/services/legal-content-writing" },
  },
  {
    slug: "ymyl",
    term: "YMYL (Your Money or Your Life)",
    category: "Content & On-Page",
    metaDescription:
      "What is YMYL? Google's term for topics that can affect people's health, finances or safety, including almost all legal content.",
    shortDefinition:
      "YMYL, short for 'Your Money or Your Life', is Google's term for topics that could significantly affect a person's health, financial stability, safety or wellbeing. Legal information falls squarely into this category, so Google expects stronger evidence of accuracy and trustworthiness.",
    whyItMatters:
      "Because legal content is YMYL, thin or generic articles are less likely to rank, and ranking volatility after core updates tends to be higher. Firms that publish carefully reviewed, attorney-backed content are better protected.",
    lawFirmExample:
      "A criminal defense firm publishes quick AI-drafted posts about sentencing ranges without attorney review. Several contain outdated statutes. After a core update, those pages lose visibility while reviewed guides hold steady.",
    howToImprove: [
      "Have a licensed attorney review every legal article.",
      "Cite statutes, court rules and official sources where relevant.",
      "Update content when laws change and show the update date.",
      "Avoid giving definitive legal advice; add appropriate disclaimers.",
    ],
    relatedTerms: ["e-e-a-t", "content-pruning", "attorney-advertising-disclaimer"],
    service: { label: "Legal Content Writing", href: "/services/legal-content-writing" },
  },
  {
    slug: "search-intent",
    term: "Search Intent",
    category: "Content & On-Page",
    metaDescription:
      "What is search intent? The goal behind a search query, and how matching it decides which law firm pages rank.",
    shortDefinition:
      "Search intent is the goal behind a search query: whether the person wants information, a specific website, to compare options, or to take action such as hiring a lawyer. Google ranks pages that best match the dominant intent of a query.",
    whyItMatters:
      "'How long does probate take' and 'probate lawyer near me' need different pages. A practice area page will struggle to rank for an informational question, and a blog post will struggle to rank for a hiring query. Matching intent is often the difference between page one and page three.",
    lawFirmExample:
      "An estate planning firm tries to rank its service page for 'what is a living trust'. The results are all explanatory guides, so the firm publishes a dedicated guide and links it to the service page instead.",
    howToImprove: [
      "Check what already ranks for a query before choosing the page type.",
      "Use service pages for hiring intent and guides for question intent.",
      "Link informational content to the matching practice area page.",
      "Do not target the same keyword with multiple pages.",
    ],
    relatedTerms: ["practice-area-page", "topic-cluster"],
    service: { label: "Law Firm SEO", href: "/services/law-firm-seo" },
    blog: { label: "Law Firm SEO Strategy", href: "/blog/law-firm-seo-strategy" },
  },
  {
    slug: "topic-cluster",
    term: "Topic Cluster",
    category: "Content & On-Page",
    metaDescription:
      "What is a topic cluster? A pillar page linked to supporting articles, used to build topical authority in a practice area.",
    shortDefinition:
      "A topic cluster is a group of related pages built around one core subject: a central pillar page, usually a practice area page, supported by articles that answer specific questions and link back to it. The structure shows search engines depth of coverage on a topic.",
    whyItMatters:
      "A firm with one practice area page and nothing supporting it competes on a single page. A firm with a practice area page plus a dozen genuinely useful supporting guides demonstrates depth, earns more long-tail traffic and passes internal link value to the page that converts.",
    lawFirmExample:
      "A bankruptcy firm builds a Chapter 7 cluster: the Chapter 7 service page plus guides on the means test, exempt property, timelines and credit after bankruptcy. Each guide links back to the service page.",
    howToImprove: [
      "Map each practice area to 8–15 real client questions.",
      "Write one focused article per question.",
      "Link every article to the pillar page with descriptive anchor text.",
      "Link the pillar page out to its supporting articles.",
    ],
    relatedTerms: ["practice-area-page", "search-intent", "anchor-text"],
    service: { label: "Legal Content Writing", href: "/services/legal-content-writing" },
  },
  {
    slug: "content-pruning",
    term: "Content Pruning",
    category: "Content & On-Page",
    metaDescription:
      "What is content pruning? Updating, merging or removing weak law firm pages to improve overall site quality.",
    shortDefinition:
      "Content pruning is the process of reviewing a website's existing pages and updating, merging, redirecting or removing those that are outdated, thin, duplicated or getting no traffic. The goal is a smaller set of stronger pages that better serve visitors.",
    whyItMatters:
      "Many law firm blogs contain years of short, near-identical posts written for keywords rather than clients. Google evaluates overall site quality, so large amounts of low-value content can hold back the pages that matter.",
    lawFirmExample:
      "A personal injury firm has 40 short posts about car accidents in different years. It merges them into five comprehensive guides, redirects the old URLs, and sees the merged guides outrank all of the originals.",
    howToImprove: [
      "Export all URLs with their traffic, rankings and backlinks.",
      "Update pages that are relevant but outdated.",
      "Merge overlapping posts and 301-redirect the old URLs.",
      "Remove pages with no value, no traffic and no links.",
    ],
    relatedTerms: ["crawl-budget", "ymyl", "topic-cluster"],
    service: { label: "Legal Content Writing", href: "/services/legal-content-writing" },
  },

  // ───────────────────────── LINK BUILDING ─────────────────────────
  {
    slug: "backlink",
    term: "Backlink",
    category: "Link Building",
    metaDescription:
      "What is a backlink? A link from another website to your law firm's site, and why quality matters far more than quantity.",
    shortDefinition:
      "A backlink is a link from another website to yours. Search engines treat relevant, editorially earned backlinks as a signal that other sites trust your content. For law firms, links from legal publications, local news, bar associations and universities carry the most weight.",
    whyItMatters:
      "In competitive legal markets, the firms that rank at the top usually have stronger link profiles than those below them. But quality matters far more than quantity: a single link from a respected local newspaper can be worth more than hundreds of directory or spam links.",
    lawFirmExample:
      "A medical malpractice firm publishes original analysis of state malpractice claim data. Local news outlets and a law school blog cite it, earning links that lift the firm's practice pages for competitive terms.",
    howToImprove: [
      "Create resources others want to cite: data, guides, local legal analysis.",
      "Offer attorney commentary to journalists covering legal topics.",
      "Earn links from bar associations, local organisations and sponsorships.",
      "Avoid buying links or using link networks.",
    ],
    relatedTerms: ["anchor-text", "domain-authority", "pbn", "unlinked-mention"],
    service: { label: "Legal Link Building", href: "/services/link-building-for-law-firms" },
  },
  {
    slug: "anchor-text",
    term: "Anchor Text",
    category: "Link Building",
    metaDescription:
      "What is anchor text? The clickable words in a link, and how to use it naturally in law firm SEO.",
    shortDefinition:
      "Anchor text is the visible, clickable text of a hyperlink. It gives users and search engines context about the page being linked to. For example, a link reading 'Houston car accident lawyer' signals what the destination page is about.",
    whyItMatters:
      "Descriptive internal anchor text helps Google understand your pages. But an unnatural pattern of exact-match anchors in external backlinks, such as dozens of links all reading 'best DUI lawyer Chicago', is a classic sign of manipulative link building and can attract penalties.",
    lawFirmExample:
      "A DUI firm's internal links all say 'click here'. Changing them to descriptive anchors such as 'first-offense DUI defense' helps Google connect its supporting guides to the right service pages.",
    howToImprove: [
      "Use descriptive anchors for internal links.",
      "Vary anchor text naturally across the site.",
      "Let external sites link with natural wording such as your firm name.",
      "Audit backlinks for over-optimised exact-match anchors.",
    ],
    relatedTerms: ["backlink", "topic-cluster", "pbn"],
    service: { label: "Legal Link Building", href: "/services/link-building-for-law-firms" },
  },
  {
    slug: "pbn",
    term: "PBN (Private Blog Network)",
    category: "Link Building",
    metaDescription:
      "What is a PBN? A network of sites built to create backlinks, why Google treats it as link spam, and the risk to law firms.",
    shortDefinition:
      "A private blog network (PBN) is a group of websites, often built on expired domains, that exist mainly to link to other sites and manipulate rankings. Google's spam policies classify this as link spam, and sites relying on PBN links risk ranking drops or manual actions.",
    whyItMatters:
      "Legal SEO is competitive and lucrative, so some agencies sell PBN links as a shortcut. The results can look good for months and then collapse after a spam update, often leaving the firm with a damaged domain and a costly clean-up.",
    lawFirmExample:
      "A firm's previous agency built hundreds of links from unrelated blogs with thin articles. After a spam update the firm's rankings fall sharply, and recovery requires removing links and rebuilding its link profile ethically.",
    howToImprove: [
      "Ask any agency exactly where its links come from.",
      "Check linking sites for real audiences, real authors and topical relevance.",
      "Remove or disavow manipulative links if you have a manual action.",
      "Invest in earned links from real publications instead.",
    ],
    relatedTerms: ["backlink", "anchor-text", "domain-authority"],
    service: { label: "Legal Link Building", href: "/services/link-building-for-law-firms" },
  },
  {
    slug: "domain-authority",
    term: "Domain Authority (DA)",
    category: "Link Building",
    metaDescription:
      "What is Domain Authority? A third-party score estimating a site's ranking strength, and why Google does not use it.",
    shortDefinition:
      "Domain Authority is a score from 1 to 100 created by the SEO tool Moz to estimate how likely a website is to rank, based mainly on its backlink profile. Similar metrics include Ahrefs' Domain Rating. Google does not use these scores; they are comparison tools only.",
    whyItMatters:
      "DA is useful for comparing your firm against competitors or judging a potential link source. It becomes a problem when agencies sell 'high DA links' as the goal, because scores can be inflated and a relevant local link often matters more than a high-DA unrelated one.",
    lawFirmExample:
      "A tax law firm compares itself with three top-ranking competitors and sees a large DA gap. Instead of chasing the number, it focuses on earning links from accounting associations and business publications its clients actually read.",
    howToImprove: [
      "Use DA or DR to benchmark, not as a KPI on its own.",
      "Prioritise relevance and real traffic when judging link sources.",
      "Track rankings and consultations rather than authority scores.",
      "Be wary of anyone selling links by DA.",
    ],
    relatedTerms: ["backlink", "pbn"],
    service: { label: "Legal Link Building", href: "/services/link-building-for-law-firms" },
  },
  {
    slug: "unlinked-mention",
    term: "Unlinked Mention",
    category: "Link Building",
    metaDescription:
      "What is an unlinked mention? When a site names your law firm or attorney without linking, and how to turn it into a backlink.",
    shortDefinition:
      "An unlinked mention is a reference to a law firm or attorney on another website that does not include a link back to the firm's site. Because the publisher already knows and cites the firm, these mentions are often the easiest backlinks to earn.",
    whyItMatters:
      "Attorneys are regularly quoted in news stories, featured in bar publications and named in event listings without a link. Politely asking for a link turns existing recognition into ranking value with very little effort.",
    lawFirmExample:
      "An employment attorney is quoted in a local business journal about new overtime rules, but the article only names the firm. A short email to the editor results in a link to the attorney's bio page.",
    howToImprove: [
      "Set up alerts for your firm name and attorney names.",
      "Review mentions in news, bar publications and event pages.",
      "Send a brief, polite request to add a link.",
      "Suggest the most relevant page, such as an attorney bio.",
    ],
    relatedTerms: ["backlink", "entity-seo"],
    service: { label: "Legal Link Building", href: "/services/link-building-for-law-firms" },
  },

  // ───────────────────────── AI SEARCH & GEO ─────────────────────────
  {
    slug: "geo",
    term: "GEO (Generative Engine Optimization)",
    category: "AI Search & GEO",
    metaDescription:
      "What is GEO? Generative Engine Optimization explained for law firms: getting cited by ChatGPT, Perplexity, Gemini and AI Overviews.",
    shortDefinition:
      "Generative Engine Optimization (GEO) is the practice of making a website's content more likely to be found, cited and recommended by AI-powered search tools such as ChatGPT, Perplexity, Gemini and Google's AI Overviews. It builds on traditional SEO rather than replacing it.",
    whyItMatters:
      "More people now ask AI tools questions like 'what should I do after a car accident' or 'best immigration lawyer in Houston'. These tools summarise and cite sources. Firms whose content is clear, trustworthy and widely referenced are more likely to be named in those answers.",
    lawFirmExample:
      "An immigration firm restructures its green card guides with clear question headings, short direct answers and attorney-reviewed detail. It then starts appearing as a cited source in AI answers about family-based green card timelines.",
    howToImprove: [
      "Answer common client questions directly in the first lines of each section.",
      "Keep firm, attorney and location facts consistent across the web.",
      "Earn mentions on trusted legal and local sites.",
      "Make sure content is crawlable and not hidden behind scripts.",
    ],
    relatedTerms: ["ai-overviews", "llm-citation", "entity-seo", "llms-txt"],
    service: { label: "AI SEO & GEO", href: "/services/ai-seo-geo" },
  },
  {
    slug: "ai-overviews",
    term: "AI Overviews",
    category: "AI Search & GEO",
    metaDescription:
      "What are Google AI Overviews? AI-generated summaries at the top of Google results, and how law firms can be cited in them.",
    shortDefinition:
      "AI Overviews are AI-generated summaries that Google shows at the top of some search results, combining information from multiple web pages with links to the sources. They appear most often for informational questions rather than direct 'hire a lawyer' searches.",
    whyItMatters:
      "For many legal questions, the AI Overview answers the basics before a searcher scrolls to organic results, which can reduce clicks to informational articles. Being one of the cited sources keeps the firm visible at the moment a person is first learning about their legal problem.",
    lawFirmExample:
      "For 'how is child support calculated in Illinois', an AI Overview summarises the formula and cites three sources. A Chicago family law firm with a clear, attorney-reviewed guide is one of them and gets the brand exposure.",
    howToImprove: [
      "Target specific questions with concise, accurate answers.",
      "Use clear headings that match how people phrase questions.",
      "Support answers with statutes or official sources.",
      "Track AI Overview appearances for your key informational queries.",
    ],
    relatedTerms: ["geo", "llm-citation", "search-intent"],
    service: { label: "AI SEO & GEO", href: "/services/ai-seo-geo" },
  },
  {
    slug: "llm-citation",
    term: "LLM Citation",
    category: "AI Search & GEO",
    metaDescription:
      "What is an LLM citation? When an AI assistant names or links your law firm as a source, and why it matters for visibility.",
    shortDefinition:
      "An LLM citation is when a large language model tool, such as ChatGPT, Perplexity or Gemini, names or links a website as a source or recommendation in its answer. For law firms, it can mean being cited for legal information or recommended as a firm to contact.",
    whyItMatters:
      "AI citations act like a new form of visibility and trust. A potential client who sees a firm recommended by an AI tool may go straight to the firm's website or search its name, which often shows up as direct or branded traffic rather than an obvious referral.",
    lawFirmExample:
      "A bankruptcy firm notices a rise in branded searches and calls. In GA4 it also sees referral sessions from chatgpt.com and perplexity.ai landing on its Chapter 7 guide, which is being cited in AI answers.",
    howToImprove: [
      "Track AI referral sources in GA4 with a custom channel group.",
      "Test prompts your clients might use and note which firms are cited.",
      "Strengthen content and mentions on the topics where competitors are cited.",
      "Keep your firm's key facts consistent everywhere.",
    ],
    relatedTerms: ["geo", "ai-overviews", "entity-seo"],
    service: { label: "AI SEO & GEO", href: "/services/ai-seo-geo" },
  },
  {
    slug: "entity-seo",
    term: "Entity SEO",
    category: "AI Search & GEO",
    metaDescription:
      "What is entity SEO? Helping Google and AI understand your law firm and attorneys as distinct, trusted real-world entities.",
    shortDefinition:
      "Entity SEO is the practice of helping search engines and AI systems recognise a law firm, its attorneys and its locations as distinct real-world 'entities', with clear attributes and connections. It relies on consistent information, structured data and mentions across trusted sources.",
    whyItMatters:
      "Google and AI tools increasingly reason about who someone is, not just which keywords a page contains. A firm whose attorneys are clearly connected to their bar admissions, publications, speaking events and firm profile is easier to trust and recommend.",
    lawFirmExample:
      "A corporate law firm links each attorney bio to their state bar profile, LinkedIn, published articles and speaking engagements using consistent names and sameAs schema. The attorneys become clearly associated with their practice areas.",
    howToImprove: [
      "Use the same firm and attorney names everywhere.",
      "Add Organization, LegalService and Person schema with sameAs links.",
      "Build detailed attorney bio pages.",
      "Earn mentions on authoritative legal and local sites.",
    ],
    relatedTerms: ["schema-markup", "e-e-a-t", "geo"],
    service: { label: "AI SEO & GEO", href: "/services/ai-seo-geo" },
  },
  {
    slug: "llms-txt",
    term: "llms.txt",
    category: "AI Search & GEO",
    metaDescription:
      "What is llms.txt? A proposed file that summarises a website for AI tools. What it does, what it doesn't, and whether law firms need it.",
    shortDefinition:
      "llms.txt is a proposed standard: a plain-text Markdown file placed at a website's root that gives AI tools a short summary of the site and links to its most important pages. It is not an official Google standard, and major AI search engines have not confirmed relying on it.",
    whyItMatters:
      "It is low effort to add and may help some AI tools and agents understand a site more quickly. But it should not be sold as a ranking factor or a substitute for crawlable, high-quality content, which remains what AI search actually draws from.",
    lawFirmExample:
      "A personal injury firm adds an llms.txt listing its practice areas, office locations and top guides. It treats it as a small housekeeping step while focusing its GEO effort on content quality and mentions.",
    howToImprove: [
      "Keep it short and accurate: firm summary, practice areas, key URLs.",
      "Update it when major pages change.",
      "Do not rely on it instead of good content and technical SEO.",
      "Make sure robots.txt does not block AI crawlers you want to allow.",
    ],
    relatedTerms: ["geo", "indexing"],
    service: { label: "AI SEO & GEO", href: "/services/ai-seo-geo" },
  },

  // ───────────────────────── PPC & PAID SEARCH ─────────────────────────
  {
    slug: "local-services-ads",
    term: "Local Services Ads (LSA)",
    category: "PPC & Paid Search",
    metaDescription:
      "What are Local Services Ads for lawyers? Google's pay-per-lead ads with the Google Screened badge, explained for law firms.",
    shortDefinition:
      "Local Services Ads are Google ads that appear at the very top of some local searches. Law firms that pass Google's screening can show a 'Google Screened' badge. Unlike standard search ads, firms generally pay per lead, such as a call or message, rather than per click.",
    whyItMatters:
      "LSAs often sit above both standard ads and the Local Pack, so they capture high-intent searchers first. Rankings within LSAs are influenced by reviews, responsiveness and proximity, so fast intake and good reviews directly affect results.",
    lawFirmExample:
      "A DUI firm activates LSAs and answers calls within minutes around the clock. Its responsiveness and reviews help it appear more often than competitors who let LSA calls go to voicemail.",
    howToImprove: [
      "Complete Google's screening and license verification.",
      "Answer LSA calls and messages quickly, including after hours.",
      "Dispute leads that are clearly not valid.",
      "Keep earning reviews, which also feed LSA ranking.",
    ],
    relatedTerms: ["cost-per-click", "review-velocity", "cost-per-case"],
    service: { label: "PPC for Law Firms", href: "/services/ppc-for-law-firms" },
  },
  {
    slug: "cost-per-click",
    term: "Cost Per Click (CPC)",
    category: "PPC & Paid Search",
    metaDescription:
      "What is cost per click? What law firms pay for each ad click, and why legal CPCs are among the highest in search advertising.",
    shortDefinition:
      "Cost per click (CPC) is the amount an advertiser pays each time someone clicks their search ad. Legal keywords, especially in personal injury and mass torts, have some of the highest CPCs in search advertising because each signed case can be worth a large fee.",
    whyItMatters:
      "High CPCs mean poorly targeted campaigns burn budget quickly. It also explains why organic SEO is valuable for law firms: every organic click on a competitive keyword is a click the firm did not have to pay for.",
    lawFirmExample:
      "A truck accident firm finds much of its ad spend going to broad searches such as 'truck accident statistics'. Adding negative keywords and focusing on hiring-intent searches cuts wasted spend.",
    howToImprove: [
      "Target hiring-intent keywords and add negative keywords.",
      "Send ads to relevant landing pages, not the homepage.",
      "Improve Quality Score to lower costs.",
      "Measure cost per signed case, not just CPC.",
    ],
    relatedTerms: ["quality-score", "cost-per-case", "local-services-ads"],
    service: { label: "PPC for Law Firms", href: "/services/ppc-for-law-firms" },
  },
  {
    slug: "quality-score",
    term: "Quality Score",
    category: "PPC & Paid Search",
    metaDescription:
      "What is Quality Score? Google Ads' 1–10 rating of ad relevance and landing page experience, and how it affects law firm ad costs.",
    shortDefinition:
      "Quality Score is a 1–10 diagnostic rating in Google Ads for each keyword, based on expected click-through rate, ad relevance and landing page experience. Higher scores generally mean better ad positions at a lower cost per click.",
    whyItMatters:
      "In expensive legal auctions, a small improvement in Quality Score can meaningfully lower costs. Many law firm campaigns score poorly because every ad sends traffic to a generic homepage instead of a page matching the search.",
    lawFirmExample:
      "A workers' compensation firm creates dedicated landing pages for its main injury types and writes ads matching each keyword group. Its Quality Scores improve and its cost per click drops.",
    howToImprove: [
      "Group closely related keywords into tight ad groups.",
      "Write ads that reflect the searcher's exact problem.",
      "Use fast, relevant landing pages with a clear call to action.",
      "Review search terms regularly and remove irrelevant ones.",
    ],
    relatedTerms: ["cost-per-click", "core-web-vitals", "intake-conversion-rate"],
    service: { label: "PPC for Law Firms", href: "/services/ppc-for-law-firms" },
  },

  // ───────────────────────── LEADS & ANALYTICS ─────────────────────────
  {
    slug: "cost-per-case",
    term: "Cost Per Case",
    category: "Leads & Analytics",
    metaDescription:
      "What is cost per case? The marketing cost of each signed client, and why it is the metric law firms should judge SEO by.",
    shortDefinition:
      "Cost per case, or cost per signed case, is the total marketing spend for a channel divided by the number of clients signed from that channel in the same period. It is the most meaningful measure of whether SEO, PPC or any other marketing is paying off for a law firm.",
    whyItMatters:
      "Traffic, rankings and even leads can look good while signed cases stay flat. Cost per case connects marketing to revenue and lets a firm compare channels fairly. SEO often has a high cost per case early on and a falling one as rankings compound.",
    lawFirmExample:
      "A personal injury firm spends the same amount on SEO and PPC in a quarter. PPC produces more leads, but SEO produces more signed cases, giving SEO a lower cost per case.",
    howToImprove: [
      "Track the source of every lead through to signed or not signed.",
      "Connect your intake or CRM system to marketing data.",
      "Compare cost per case by channel and practice area.",
      "Review it over at least a 6–12 month window for SEO.",
    ],
    relatedTerms: ["intake-conversion-rate", "call-tracking", "ga4-key-event"],
    service: { label: "Lead Generation for Law Firms", href: "/services/lead-generation-for-law-firms" },
    blog: { label: "Can SEO Help Law Firms Generate More Leads?", href: "/blog/can-seo-help-law-firms-generate-more-leads" },
  },
  {
    slug: "intake-conversion-rate",
    term: "Intake Conversion Rate",
    category: "Leads & Analytics",
    metaDescription:
      "What is intake conversion rate? The share of law firm enquiries that become consultations or signed cases, and how to raise it.",
    shortDefinition:
      "Intake conversion rate is the percentage of enquiries, such as calls, forms and chats, that turn into consultations or signed clients. It measures how well a law firm's intake process turns marketing leads into business.",
    whyItMatters:
      "SEO can double enquiries, but if calls go unanswered or follow-up is slow, much of that value is lost. Improving intake is often the fastest way to increase signed cases without spending more on marketing.",
    lawFirmExample:
      "A family law firm discovers that after-hours calls go to a full voicemail box. Adding an answering service and a same-day callback rule noticeably raises its intake conversion rate.",
    howToImprove: [
      "Answer calls live, including evenings and weekends where possible.",
      "Respond to web forms within minutes, not hours.",
      "Use a consistent intake script and qualification checklist.",
      "Record the outcome of every enquiry.",
    ],
    relatedTerms: ["cost-per-case", "call-tracking"],
    service: { label: "Lead Generation for Law Firms", href: "/services/lead-generation-for-law-firms" },
  },
  {
    slug: "call-tracking",
    term: "Call Tracking",
    category: "Leads & Analytics",
    metaDescription:
      "What is call tracking? How law firms measure which marketing channels drive phone calls, without breaking local SEO.",
    shortDefinition:
      "Call tracking uses unique phone numbers, often assigned dynamically on the website, to show which marketing source generated each phone call. For law firms, where most high-value enquiries arrive by phone, it is essential for measuring real results.",
    whyItMatters:
      "Without call tracking, a firm cannot tell whether SEO, PPC, LSAs or directories are producing clients. Set up carelessly, however, it can create NAP inconsistencies across listings and weaken local SEO.",
    lawFirmExample:
      "A criminal defense firm uses dynamic number insertion on its website so organic, paid and direct visitors see different numbers. Its Google Business Profile uses a tracking number as primary with the main line as an additional number.",
    howToImprove: [
      "Use dynamic number insertion on the website.",
      "Keep your main number in the HTML for consistency.",
      "On GBP, add the main line as an additional phone number.",
      "Send call events into GA4 as key events.",
    ],
    relatedTerms: ["nap-consistency", "ga4-key-event", "cost-per-case"],
    service: { label: "Analytics & Reporting", href: "/services/law-firm-marketing-analytics" },
  },
  {
    slug: "ga4-key-event",
    term: "GA4 Key Event",
    category: "Leads & Analytics",
    metaDescription:
      "What is a GA4 key event? How law firms mark calls, forms and chats as the actions that matter in Google Analytics 4.",
    shortDefinition:
      "A key event is an action in Google Analytics 4 that a business marks as important, such as a phone call click, form submission or chat start. Google renamed GA4 'conversions' to 'key events' in 2024; 'conversions' now refers to events imported into Google Ads.",
    whyItMatters:
      "Reporting on pageviews and sessions alone says little about whether marketing works. Marking the right actions as key events lets a firm see which pages and channels produce enquiries, and is the first step toward measuring cost per case.",
    lawFirmExample:
      "A law firm tracks clicks on its phone number, WhatsApp button and consultation form through Google Tag Manager and marks each as a key event in GA4. It can now see which practice area pages generate enquiries.",
    howToImprove: [
      "Track calls, form submissions and chat starts as events.",
      "Mark only meaningful actions as key events.",
      "Test events in GA4 DebugView before relying on them.",
      "Import key events into Google Ads if you run paid campaigns.",
    ],
    relatedTerms: ["call-tracking", "cost-per-case", "intake-conversion-rate"],
    service: { label: "Analytics & Reporting", href: "/services/law-firm-marketing-analytics" },
  },

  // ───────────────────────── BAR COMPLIANCE ─────────────────────────
  {
    slug: "aba-model-rule-7-1",
    term: "ABA Model Rule 7.1",
    category: "Bar Compliance",
    metaDescription:
      "What is ABA Model Rule 7.1? The rule against false or misleading lawyer communications, and what it means for law firm SEO content.",
    shortDefinition:
      "ABA Model Rule 7.1 prohibits lawyers from making false or misleading communications about themselves or their services. Most US states have adopted a version of it, and it applies to websites, blog posts, ads, profiles and other marketing, including content written by an agency.",
    whyItMatters:
      "SEO copy often uses claims such as 'best', 'top-rated' or 'guaranteed results'. Depending on the state, these can be misleading or unverifiable. Because responsibility sits with the lawyer, not the agency, non-compliant marketing puts the firm's license at risk.",
    lawFirmExample:
      "A PI firm's agency writes 'We win every case' on the homepage. The firm replaces it with specific, verifiable results and a clear note that past results do not guarantee future outcomes.",
    howToImprove: [
      "Avoid superlatives and guarantees you cannot substantiate.",
      "Present case results with context and required disclaimers.",
      "Check your state's version of the rules and ethics opinions.",
      "Have an attorney approve marketing content before publishing.",
    ],
    relatedTerms: ["attorney-advertising-disclaimer", "client-testimonial-rules", "ymyl"],
    service: { label: "Legal Content Writing", href: "/services/legal-content-writing" },
  },
  {
    slug: "attorney-advertising-disclaimer",
    term: "Attorney Advertising Disclaimer",
    category: "Bar Compliance",
    metaDescription:
      "What is an attorney advertising disclaimer? The notices some states require on law firm websites and marketing.",
    shortDefinition:
      "An attorney advertising disclaimer is a notice, such as 'Attorney Advertising' or 'Prior results do not guarantee a similar outcome', that some state bar rules require on law firm marketing. Requirements vary significantly by state and by the type of content.",
    whyItMatters:
      "A disclaimer that is missing or placed incorrectly can create a compliance problem even when the content itself is accurate. Multi-state firms and national SEO campaigns need to meet the strictest applicable rules.",
    lawFirmExample:
      "A New York firm adds 'Attorney Advertising' to its homepage and a prior-results disclaimer to every case results page, in line with New York's advertising rules.",
    howToImprove: [
      "Check the advertising rules in every state where you practise.",
      "Add required disclaimers near case results and testimonials.",
      "Include a general website disclaimer in the footer.",
      "Review disclaimers when adding new states or offices.",
    ],
    relatedTerms: ["aba-model-rule-7-1", "client-testimonial-rules"],
    service: { label: "Legal Content Writing", href: "/services/legal-content-writing" },
  },
  {
    slug: "client-testimonial-rules",
    term: "Client Testimonial Rules",
    category: "Bar Compliance",
    metaDescription:
      "What are client testimonial rules for lawyers? How state bar rules affect reviews and testimonials in law firm marketing.",
    shortDefinition:
      "Client testimonial rules are state bar requirements governing how law firms can use client reviews and testimonials in marketing. Depending on the state, they may require disclaimers, prohibit certain claims, restrict incentives for reviews, or require client consent.",
    whyItMatters:
      "Reviews are central to local SEO and conversion, so firms naturally want to feature them. But posting testimonials without required disclaimers, editing them, or rewarding clients for them can breach bar rules even when the reviews are genuine.",
    lawFirmExample:
      "A Florida firm features client quotes on its practice area pages. It checks the state's rules on testimonials and adds the required context and disclaimers before publishing.",
    howToImprove: [
      "Check your state's rules before displaying testimonials.",
      "Get written consent before quoting a client.",
      "Never offer incentives for reviews.",
      "Avoid replying to reviews in ways that reveal confidential information.",
    ],
    relatedTerms: ["review-velocity", "aba-model-rule-7-1", "attorney-advertising-disclaimer"],
    service: { label: "Legal Content Writing", href: "/services/legal-content-writing" },
  },
];

export const getTermBySlug = (slug: string) =>
  glossaryTerms.find((t) => t.slug === slug);
